import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type {
  BiltyCreate,
  BiltyList,
  BiltySummary,
  BiltySummaryApiBiltySummaryGetParams,
  BiltyPromoteBody,
  BiltyChargesUpdate,
  BiltyRead,
  BiltyUpdate,
  Bilty360List,
  BranchPLResponse,
  BranchPlApiLedgersBranchPlGetParams,
  BranchRead,
  CashSummary,
  ExpenseCategoryCreate,
  ExpenseCategoryRead,
  ExpenseCreate,
  ExpenseList,
  ExpenseRead,
  ListExpensesApiCashBookExpensesGetParams,
  CompanyProfileRead,
  CompanyProfileUpdate,
  DocumentDistributionResponse,
  DriverRead,
  ExtractResponse,
  GetBilty360ApiReportsBilty360GetParams,
  Gstr1JsonApiGstGstr1GetParams,
  Gstr3bJsonApiGstGstr3bGetParams,
  GSTR1Response,
  GSTR3BResponse,
  InvoiceBatchCreate,
  InvoiceBatchRead,
  InvoiceDetail,
  InvoiceList,
  ListBiltyApiBiltyGetParams,
  ListInvoicesApiInvoicesGetParams,
  ListLoadingSlipsApiLoadingSlipsGetParams,
  LhcCreate,
  LhcList,
  LhcOtherChargeCreate,
  LhcRead,
  LhcUpdate,
  ListLhcsApiLorryHireChallansGetParams,
  ListReceiptsApiPaymentsReceiptsGetParams,
  OpenInvoiceRow,
  OutstandingResponse,
  ReceiptCreate,
  ReceiptList,
  ReceiptRead,
  VendorPaymentCreate,
  LoadingSlipCreate,
  LoadingSlipDetail,
  LoadingSlipList,
  PartyLedgerApiLedgersPartyPartyIdGetParams,
  PartyLedgerResponse,
  PartyRead,
  TruckRead,
  UserAdminUpdate,
  UserList,
  UserMeUpdate,
  UserRead,
  VendorLedgerApiLedgersVendorVendorIdGetParams,
  VendorLedgerResponse,
} from "./generated";
import {
  adminUpdateUserApiUsersUserIdPatch,
  branchPlApiLedgersBranchPlGet,
  addLhcPaymentApiLorryHireChallansLhcIdPaymentsPost,
  addOtherChargeApiLorryHireChallansLhcIdOtherChargesPost,
  createCategoryApiCashBookCategoriesPost,
  createExpenseApiCashBookExpensesPost,
  createReceiptApiPaymentsReceiptsPost,
  deleteExpenseApiCashBookExpensesExpenseIdDelete,
  listCategoriesApiCashBookCategoriesGet,
  listExpensesApiCashBookExpensesGet,
  summaryApiCashBookSummaryGet,
  deleteLhcPaymentApiLorryHireChallansLhcIdPaymentsPaymentIdDelete,
  deleteReceiptApiPaymentsReceiptsReceiptIdDelete,
  getInvoiceApiInvoicesInvoiceIdGet,
  listReceiptsApiPaymentsReceiptsGet,
  openInvoicesApiPaymentsOpenInvoicesGet,
  outstandingApiPaymentsOutstandingGet,
  patchLhcApiLorryHireChallansLhcIdPatch,
  cancelBiltyRouteApiBiltyBiltyIdCancelPost,
  createCustomerBatchApiInvoicesCustomerBatchPost,
  createBiltyApiBiltyPost,
  createLhcApiLorryHireChallansPost,
  createLoadingSlipApiLoadingSlipsPost,
  getLhcApiLorryHireChallansLhcIdGet,
  getLoadingSlipApiLoadingSlipsSlipIdGet,
  listLhcsApiLorryHireChallansGet,
  settleLhcApiLorryHireChallansLhcIdSettlePost,
  unlinkBiltyFromSlipApiLoadingSlipsSlipIdBiltiesBiltyIdDelete,
  getBiltyApiBiltyBiltyIdGet,
  getBilty360ApiReportsBilty360Get,
  getCompanyProfileApiCompanyProfileGet,
  getDocumentDistributionApiReportsDocumentDistributionGet,
  getMeApiUsersMeGet,
  gstr1JsonApiGstGstr1Get,
  gstr3bJsonApiGstGstr3bGet,
  listBiltyApiBiltyGet,
  listBranchesApiBranchesGet,
  listDriversApiDriversGet,
  listInvoicesApiInvoicesGet,
  listLoadingSlipsApiLoadingSlipsGet,
  listPartiesApiPartiesGet,
  listTrucksApiTrucksGet,
  listUsersApiUsersGet,
  partyLedgerApiLedgersPartyPartyIdGet,
  patchBiltyApiBiltyBiltyIdPatch,
  updateBiltyChargesRouteApiBiltyBiltyIdChargesPost,
  promoteBiltyRouteApiBiltyBiltyIdPromotePost,
  extractBiltyApiBiltyExtractPost,
  PartyRole,
  RecordStatus,
  replaceCompanyProfileApiCompanyProfilePut,
  updateMeApiUsersMePatch,
  vendorLedgerApiLedgersVendorVendorIdGet,
  biltySummaryApiBiltySummaryGet,
} from "./generated";

export type BiltyStatusValue =
  | "draft"
  | "generated"
  | "in_transit"
  | "delivered"
  | "invoiced"
  | "settled";

export const biltyQueryKeys = {
  all: ["bilty"] as const,
  list: (params: ListBiltyApiBiltyGetParams) => ["bilty", "list", params] as const,
  detail: (id: string) => ["bilty", id] as const,
  summary: (params: BiltySummaryApiBiltySummaryGetParams) => ["bilty", "summary", params] as const,
};

/** Tiles + pill counts over the whole filtered range, not one page (V53). */
export function useBiltySummary(
  params: BiltySummaryApiBiltySummaryGetParams,
): UseQueryResult<BiltySummary, Error> {
  return useQuery({
    queryKey: biltyQueryKeys.summary(params),
    queryFn: async () => {
      const res = await biltySummaryApiBiltySummaryGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load bilty summary (${res.status})`);
      return res.data;
    },
  });
}

export function useBilties(
  params: ListBiltyApiBiltyGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<BiltyList, Error> {
  return useQuery({
    queryKey: biltyQueryKeys.list(params),
    queryFn: async () => {
      const res = await listBiltyApiBiltyGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load bilties (${res.status})`);
      return res.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export const loadingSlipQueryKeys = {
  all: ["loading-slips"] as const,
  list: (p: ListLoadingSlipsApiLoadingSlipsGetParams) =>
    ["loading-slips", "list", p] as const,
  detail: (id: string) => ["loading-slips", id] as const,
};

export function useLoadingSlipDetail(
  id: string | undefined,
): UseQueryResult<LoadingSlipDetail | null, Error> {
  return useQuery({
    queryKey: loadingSlipQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await getLoadingSlipApiLoadingSlipsSlipIdGet(id!, {
        credentials: "include",
      });
      const status = res.status as number;
      if (status === 404) return null;
      if (status !== 200) throw new Error(`Failed to load truck challan (${status})`);
      return res.data as LoadingSlipDetail;
    },
    enabled: Boolean(id),
  });
}

export function useCreateLoadingSlip(): UseMutationResult<
  { id: string; slip_no: string },
  Error,
  LoadingSlipCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LoadingSlipCreate) => {
      const res = await createLoadingSlipApiLoadingSlipsPost(body, {
        credentials: "include",
      });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Create truck challan failed (${res.status})`);
      }
      return res.data as { id: string; slip_no: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loadingSlipQueryKeys.all });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useUnlinkBiltyFromSlip(
  slipId: string,
): UseMutationResult<unknown, Error, { biltyId: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ biltyId }) => {
      const res = await unlinkBiltyFromSlipApiLoadingSlipsSlipIdBiltiesBiltyIdDelete(
        slipId,
        biltyId,
        { credentials: "include" },
      );
      if ((res.status as number) !== 200) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Unlink failed (${res.status})`);
      }
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: loadingSlipQueryKeys.all });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useLoadingSlips(
  params: ListLoadingSlipsApiLoadingSlipsGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<LoadingSlipList, Error> {
  return useQuery({
    queryKey: loadingSlipQueryKeys.list(params),
    queryFn: async () => {
      const res = await listLoadingSlipsApiLoadingSlipsGet(params, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Failed to load loading slips (${res.status})`);
      return res.data as LoadingSlipList;
    },
    enabled: options?.enabled ?? true,
  });
}

export const invoiceQueryKeys = {
  all: ["invoices"] as const,
  list: (p: ListInvoicesApiInvoicesGetParams) => ["invoices", "list", p] as const,
  detail: (id: string) => ["invoices", id] as const,
};

export function useInvoiceDetail(
  id: string | undefined,
): UseQueryResult<InvoiceDetail | null, Error> {
  return useQuery({
    queryKey: invoiceQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await getInvoiceApiInvoicesInvoiceIdGet(id!, { credentials: "include" });
      const status = res.status as number;
      if (status === 404) return null;
      if (status !== 200) throw new Error(`Failed to load invoice (${status})`);
      return res.data as InvoiceDetail;
    },
    enabled: Boolean(id),
  });
}

// ---------------------------------------------------------------- payments (V49)

export const paymentQueryKeys = {
  all: ["payments"] as const,
  receipts: (p: ListReceiptsApiPaymentsReceiptsGetParams) =>
    ["payments", "receipts", p] as const,
  openInvoices: (partyId: string) => ["payments", "open-invoices", partyId] as const,
  outstanding: (asOf: string | undefined) => ["payments", "outstanding", asOf ?? ""] as const,
};

/** Everything a money change can move: the invoice, receipts, the bilty's
 *  derived "settled" status, LHC balances and the reports that read them. */
function invalidateMoney(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: invoiceQueryKeys.all });
  void qc.invalidateQueries({ queryKey: paymentQueryKeys.all });
  void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
  void qc.invalidateQueries({ queryKey: lhcQueryKeys.all });
  void qc.invalidateQueries({ queryKey: ["reports"] });
}

export function useOpenInvoices(
  partyId: string | null | undefined,
): UseQueryResult<OpenInvoiceRow[], Error> {
  return useQuery({
    queryKey: paymentQueryKeys.openInvoices(partyId ?? ""),
    queryFn: async () => {
      const res = await openInvoicesApiPaymentsOpenInvoicesGet(
        { party_id: partyId! },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load open invoices (${res.status})`);
      return res.data as OpenInvoiceRow[];
    },
    enabled: Boolean(partyId),
  });
}

export function useCreateReceipt(): UseMutationResult<ReceiptRead, Error, ReceiptCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReceiptCreate) => {
      const res = await createReceiptApiPaymentsReceiptsPost(body, { credentials: "include" });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Record receipt failed (${res.status})`);
      }
      return res.data as ReceiptRead;
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeleteReceipt(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (receiptId: string) => {
      const res = await deleteReceiptApiPaymentsReceiptsReceiptIdDelete(receiptId, {
        credentials: "include",
      });
      if ((res.status as number) !== 204) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Delete receipt failed (${res.status})`);
      }
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useReceipts(
  params: ListReceiptsApiPaymentsReceiptsGetParams,
): UseQueryResult<ReceiptList, Error> {
  return useQuery({
    queryKey: paymentQueryKeys.receipts(params),
    queryFn: async () => {
      const res = await listReceiptsApiPaymentsReceiptsGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load receipts (${res.status})`);
      return res.data as ReceiptList;
    },
  });
}

export function useOutstanding(asOf?: string): UseQueryResult<OutstandingResponse, Error> {
  return useQuery({
    queryKey: paymentQueryKeys.outstanding(asOf),
    queryFn: async () => {
      const res = await outstandingApiPaymentsOutstandingGet(asOf ? { as_of: asOf } : {}, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Failed to load outstanding (${res.status})`);
      return res.data as OutstandingResponse;
    },
  });
}

export function useAddLhcPayment(
  id: string,
): UseMutationResult<LhcRead, Error, VendorPaymentCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VendorPaymentCreate) => {
      const res = await addLhcPaymentApiLorryHireChallansLhcIdPaymentsPost(id, body, {
        credentials: "include",
      });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Record payment failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useDeleteLhcPayment(id: string): UseMutationResult<LhcRead, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paymentId: string) => {
      const res = await deleteLhcPaymentApiLorryHireChallansLhcIdPaymentsPaymentIdDelete(
        id,
        paymentId,
        { credentials: "include" },
      );
      if (res.status !== 200) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Delete payment failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useInvoices(
  params: ListInvoicesApiInvoicesGetParams,
): UseQueryResult<InvoiceList, Error> {
  return useQuery({
    queryKey: invoiceQueryKeys.list(params),
    queryFn: async () => {
      const res = await listInvoicesApiInvoicesGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load invoices (${res.status})`);
      return res.data as InvoiceList;
    },
  });
}

export const lhcQueryKeys = {
  all: ["lorry-hire-challans"] as const,
  list: (p: ListLhcsApiLorryHireChallansGetParams) =>
    ["lorry-hire-challans", "list", p] as const,
  detail: (id: string) => ["lorry-hire-challans", id] as const,
};

export function useLorryHireChallans(
  params: ListLhcsApiLorryHireChallansGetParams,
): UseQueryResult<LhcList, Error> {
  return useQuery({
    queryKey: lhcQueryKeys.list(params),
    queryFn: async () => {
      const res = await listLhcsApiLorryHireChallansGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load lorry hire challans (${res.status})`);
      return res.data as LhcList;
    },
  });
}

export function useLhc(id: string | undefined): UseQueryResult<LhcRead | null, Error> {
  return useQuery({
    queryKey: lhcQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await getLhcApiLorryHireChallansLhcIdGet(id!, { credentials: "include" });
      const status = res.status as number;
      if (status === 404) return null;
      if (status !== 200) throw new Error(`Failed to load LHC (${status})`);
      return res.data as LhcRead;
    },
    enabled: Boolean(id),
  });
}

export function useCreateLhc(): UseMutationResult<LhcRead, Error, LhcCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LhcCreate) => {
      const res = await createLhcApiLorryHireChallansPost(body, { credentials: "include" });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Create LHC failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lhcQueryKeys.all });
      void qc.invalidateQueries({ queryKey: loadingSlipQueryKeys.all });
    },
  });
}

export function useUpdateLhc(id: string): UseMutationResult<LhcRead, Error, LhcUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LhcUpdate) => {
      const res = await patchLhcApiLorryHireChallansLhcIdPatch(id, body, {
        credentials: "include",
      });
      if (res.status !== 200) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Update LHC failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useAddLhcOtherCharge(
  id: string,
): UseMutationResult<LhcRead, Error, LhcOtherChargeCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LhcOtherChargeCreate) => {
      const res = await addOtherChargeApiLorryHireChallansLhcIdOtherChargesPost(id, body, {
        credentials: "include",
      });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Add charge failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lhcQueryKeys.all });
    },
  });
}

export function useSettleLhc(id: string): UseMutationResult<LhcRead, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await settleLhcApiLorryHireChallansLhcIdSettlePost(id, {
        credentials: "include",
      });
      if (res.status !== 200) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Settle failed (${res.status})`);
      }
      return res.data as LhcRead;
    },
    onSuccess: () => invalidateMoney(qc),
  });
}

export function useBilty(id: string | undefined): UseQueryResult<BiltyRead | null, Error> {
  return useQuery({
    queryKey: biltyQueryKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await getBiltyApiBiltyBiltyIdGet(id!, { credentials: "include" });
      const status = res.status as number;
      if (status === 404) return null;
      if (status !== 200) throw new Error(`Failed to load bilty (${status})`);
      return res.data as BiltyRead;
    },
    enabled: Boolean(id),
  });
}

export function useCreateBilty(): UseMutationResult<BiltyRead, Error, BiltyCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BiltyCreate) => {
      const res = await createBiltyApiBiltyPost(body, { credentials: "include" });
      if (res.status !== 201) throw new Error(`Create failed (${res.status})`);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useUpdateBilty(
  id: string,
): UseMutationResult<BiltyRead, Error, BiltyUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BiltyUpdate) => {
      const res = await patchBiltyApiBiltyBiltyIdPatch(id, body, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Update failed (${res.status})`);
      return res.data as BiltyRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

/** Revise destination charges after delivery (V48 P2) — the bilty keeps the
 *  old→new note; the invoice screen just sees the new total. */
export function useUpdateBiltyCharges(): UseMutationResult<
  BiltyRead,
  Error,
  { id: string; body: BiltyChargesUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const res = await updateBiltyChargesRouteApiBiltyBiltyIdChargesPost(id, body, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Charges update failed (${res.status})`);
      return res.data as BiltyRead;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function usePromoteBilty(
  id: string,
): UseMutationResult<BiltyRead, Error, { target_status: BiltyStatusValue }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ target_status }) => {
      const body: BiltyPromoteBody = { target_status, payload: null };
      const res = await promoteBiltyRouteApiBiltyBiltyIdPromotePost(id, body, {
        credentials: "include",
      });
      if (res.status !== 200) {
        // Surface the FastAPI detail when available (409 on missing sibling docs).
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Promote failed (${res.status})`);
      }
      return res.data as BiltyRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useCancelBilty(
  id: string,
): UseMutationResult<BiltyRead, Error, { reason: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ reason }) => {
      const res = await cancelBiltyRouteApiBiltyBiltyIdCancelPost(
        id,
        { reason },
        { credentials: "include" },
      );
      if (res.status !== 200) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Cancel failed (${res.status})`);
      }
      return res.data as BiltyRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useExtractBilty(): UseMutationResult<ExtractResponse, Error, { text: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ text }) => {
      const res = await extractBiltyApiBiltyExtractPost({ text }, { credentials: "include" });
      if (res.status === 200) return res.data as ExtractResponse;
      const raw = res.data as { detail?: unknown };
      const detail = raw?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail !== undefined && detail !== null && typeof detail === "object"
            ? JSON.stringify(detail).slice(0, 240)
            : `Extract failed (${res.status})`;
      throw new Error(msg);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["parties"] });
      void qc.invalidateQueries({ queryKey: ["trucks"] });
      void qc.invalidateQueries({ queryKey: ["drivers"] });
    },
  });
}

export function useGenerateBilty(): UseMutationResult<BiltyRead, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const body: BiltyPromoteBody = { target_status: "generated", payload: null };
      const res = await promoteBiltyRouteApiBiltyBiltyIdPromotePost(id, body, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Generate failed (${res.status})`);
      return res.data;
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export const masterQueryKeys = {
  parties: (q: string) => ["parties", "combobox", q] as const,
  partiesCustomers: (q: string) => ["parties", "customers", "combobox", q] as const,
  trucks: (q: string) => ["trucks", "combobox", q] as const,
  drivers: (q: string) => ["drivers", "combobox", q] as const,
};

/** Parties for bilty comboboxes; `debouncedSearch` maps to `?q=` on the list API. */
export function usePartiesForCombobox(debouncedSearch: string): UseQueryResult<PartyRead[], Error> {
  const q = debouncedSearch.trim();
  return useQuery({
    queryKey: masterQueryKeys.parties(q),
    queryFn: async () => {
      const res = await listPartiesApiPartiesGet(
        {
          page: 1,
          page_size: 100,
          status: "active",
          ...(q ? { q } : {}),
        },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load parties (${res.status})`);
      return res.data.items;
    },
  });
}

/** Customer-role parties for tax invoice batch UI. */
export function useCustomersForCombobox(debouncedSearch: string): UseQueryResult<PartyRead[], Error> {
  const q = debouncedSearch.trim();
  return useQuery({
    queryKey: masterQueryKeys.partiesCustomers(q),
    queryFn: async () => {
      const res = await listPartiesApiPartiesGet(
        {
          page: 1,
          page_size: 100,
          status: "active",
          role: PartyRole.customer,
          ...(q ? { q } : {}),
        },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load customers (${res.status})`);
      return res.data.items;
    },
  });
}

export function useCreateCustomerBatchInvoice(): UseMutationResult<
  InvoiceBatchRead,
  Error,
  InvoiceBatchCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: InvoiceBatchCreate) => {
      const res = await createCustomerBatchApiInvoicesCustomerBatchPost(body, {
        credentials: "include",
      });
      if (res.status === 201) return res.data as InvoiceBatchRead;
      const msg = `Customer batch failed (${res.status})`;
      throw new Error(msg);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceQueryKeys.all });
      void qc.invalidateQueries({ queryKey: biltyQueryKeys.all });
    },
  });
}

export function useTrucksForCombobox(debouncedSearch: string): UseQueryResult<TruckRead[], Error> {
  const q = debouncedSearch.trim();
  return useQuery({
    queryKey: masterQueryKeys.trucks(q),
    queryFn: async () => {
      const res = await listTrucksApiTrucksGet(
        {
          page: 1,
          page_size: 100,
          status: "active",
          ...(q ? { q } : {}),
        },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load trucks (${res.status})`);
      return res.data.items;
    },
  });
}

export function useDriversForCombobox(debouncedSearch: string): UseQueryResult<DriverRead[], Error> {
  const q = debouncedSearch.trim();
  return useQuery({
    queryKey: masterQueryKeys.drivers(q),
    queryFn: async () => {
      const res = await listDriversApiDriversGet(
        {
          page: 1,
          page_size: 100,
          status: "active",
          ...(q ? { q } : {}),
        },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load drivers (${res.status})`);
      return res.data.items;
    },
  });
}

export const reportsQueryKeys = {
  bilty360: (params: GetBilty360ApiReportsBilty360GetParams) => ["reports", "bilty-360", params] as const,
  documentDistribution: (fy: number) => ["reports", "document-distribution", fy] as const,
};

export function useBilty360(
  params: GetBilty360ApiReportsBilty360GetParams,
  options?: { enabled?: boolean },
): UseQueryResult<Bilty360List, Error> {
  return useQuery({
    queryKey: reportsQueryKeys.bilty360(params),
    queryFn: async () => {
      const res = await getBilty360ApiReportsBilty360Get(params, { credentials: "include" });
      const status = res.status as number;
      if (status === 401 || status === 403) {
        throw new Error("You need an admin session to view Bilty-360.");
      }
      if (status !== 200) throw new Error(`Bilty-360 failed (${status})`);
      return res.data as Bilty360List;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useDocumentDistribution(
  fyStartYear: number,
  options?: { enabled?: boolean },
): UseQueryResult<DocumentDistributionResponse, Error> {
  return useQuery({
    queryKey: reportsQueryKeys.documentDistribution(fyStartYear),
    queryFn: async () => {
      const res = await getDocumentDistributionApiReportsDocumentDistributionGet(
        { fy_start_year: fyStartYear },
        { credentials: "include" },
      );
      const status = res.status as number;
      if (status === 401 || status === 403) {
        throw new Error("You need an admin session for document distribution.");
      }
      if (status !== 200) throw new Error(`Document distribution failed (${status})`);
      return res.data as DocumentDistributionResponse;
    },
    enabled: options?.enabled ?? true,
  });
}

export const COMPANY_PROFILE_QK = ["company-profile"] as const;

export function useCompanyProfile(): UseQueryResult<CompanyProfileRead, Error> {
  return useQuery({
    queryKey: COMPANY_PROFILE_QK,
    queryFn: async () => {
      const res = await getCompanyProfileApiCompanyProfileGet({ credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed (${res.status})`);
      return res.data as CompanyProfileRead;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateCompanyProfile(): UseMutationResult<
  CompanyProfileRead,
  Error,
  CompanyProfileUpdate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CompanyProfileUpdate) => {
      const res = await replaceCompanyProfileApiCompanyProfilePut(body, {
        credentials: "include",
      });
      const status = res.status as number;
      if (status === 403) throw new Error("Only admins can edit the company profile.");
      if (status !== 200) throw new Error(`Update failed (${status})`);
      return res.data as CompanyProfileRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMPANY_PROFILE_QK });
    },
  });
}

export const USER_ME_QK = ["users", "me"] as const;

export function useUserMe(options?: { enabled?: boolean }): UseQueryResult<UserRead, Error> {
  return useQuery({
    queryKey: USER_ME_QK,
    queryFn: async () => {
      const res = await getMeApiUsersMeGet({ credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed (${res.status})`);
      return res.data as UserRead;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateMe(): UseMutationResult<UserRead, Error, UserMeUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserMeUpdate) => {
      const res = await updateMeApiUsersMePatch(body, { credentials: "include" });
      const status = res.status as number;
      if (status !== 200) throw new Error(`Update failed (${status})`);
      return res.data as UserRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USER_ME_QK });
    },
  });
}

export const USERS_LIST_QK = ["users", "list"] as const;

export function useUsersList(options?: {
  enabled?: boolean;
}): UseQueryResult<UserRead[], Error> {
  return useQuery({
    queryKey: USERS_LIST_QK,
    queryFn: async () => {
      const res = await listUsersApiUsersGet(
        { page: 1, page_size: 200 },
        { credentials: "include" },
      );
      const status = res.status as number;
      if (status === 401 || status === 403) {
        throw new Error("You need an admin session to list users.");
      }
      if (status !== 200) throw new Error(`Failed (${status})`);
      return (res.data as UserList).items as UserRead[];
    },
    enabled: options?.enabled ?? true,
  });
}

export const ledgerQueryKeys = {
  party: (partyId: string, params: PartyLedgerApiLedgersPartyPartyIdGetParams) =>
    ["ledgers", "party", partyId, params] as const,
  vendor: (vendorId: string, params: VendorLedgerApiLedgersVendorVendorIdGetParams) =>
    ["ledgers", "vendor", vendorId, params] as const,
  branchPl: (params: BranchPlApiLedgersBranchPlGetParams) =>
    ["ledgers", "branch-pl", params] as const,
};

export function usePartyLedger(
  partyId: string | null,
  params: PartyLedgerApiLedgersPartyPartyIdGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<PartyLedgerResponse, Error> {
  return useQuery({
    queryKey: ledgerQueryKeys.party(partyId ?? "", params),
    queryFn: async () => {
      if (!partyId) throw new Error("Pick a party first.");
      const res = await partyLedgerApiLedgersPartyPartyIdGet(partyId, params, {
        credentials: "include",
      });
      const status = res.status as number;
      if (status === 403) throw new Error("You don't have access to that party's ledger.");
      if (status === 404) throw new Error("Party not found.");
      if (status !== 200) throw new Error(`Party ledger failed (${status})`);
      return res.data as PartyLedgerResponse;
    },
    enabled: !!partyId && (options?.enabled ?? true),
  });
}

export function useVendorLedger(
  vendorId: string | null,
  params: VendorLedgerApiLedgersVendorVendorIdGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<VendorLedgerResponse, Error> {
  return useQuery({
    queryKey: ledgerQueryKeys.vendor(vendorId ?? "", params),
    queryFn: async () => {
      if (!vendorId) throw new Error("Pick a vendor first.");
      const res = await vendorLedgerApiLedgersVendorVendorIdGet(vendorId, params, {
        credentials: "include",
      });
      const status = res.status as number;
      if (status === 403) throw new Error("You don't have access to that vendor's ledger.");
      if (status === 404) throw new Error("Vendor not found.");
      if (status !== 200) throw new Error(`Vendor ledger failed (${status})`);
      return res.data as VendorLedgerResponse;
    },
    enabled: !!vendorId && (options?.enabled ?? true),
  });
}

export function useBranchPL(
  params: BranchPlApiLedgersBranchPlGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<BranchPLResponse, Error> {
  return useQuery({
    queryKey: ledgerQueryKeys.branchPl(params),
    queryFn: async () => {
      const res = await branchPlApiLedgersBranchPlGet(params, { credentials: "include" });
      const status = res.status as number;
      if (status === 403) throw new Error("You don't have access to that branch's P&L.");
      if (status === 404) throw new Error("Branch not found.");
      if (status !== 200) throw new Error(`Branch P&L failed (${status})`);
      return res.data as BranchPLResponse;
    },
    enabled: options?.enabled ?? true,
  });
}

export const gstQueryKeys = {
  gstr1: (p: Gstr1JsonApiGstGstr1GetParams) => ["gst", "gstr1", p] as const,
  gstr3b: (p: Gstr3bJsonApiGstGstr3bGetParams) => ["gst", "gstr3b", p] as const,
};

export function useGstr1(
  params: Gstr1JsonApiGstGstr1GetParams,
  options?: { enabled?: boolean },
): UseQueryResult<GSTR1Response, Error> {
  return useQuery({
    queryKey: gstQueryKeys.gstr1(params),
    queryFn: async () => {
      const res = await gstr1JsonApiGstGstr1Get(params, { credentials: "include" });
      const status = res.status as number;
      if (status === 401 || status === 403) {
        throw new Error("You need an admin session to view GSTR-1.");
      }
      if (status === 400) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || "Invalid period.");
      }
      if (status !== 200) throw new Error(`GSTR-1 failed (${status})`);
      return res.data as GSTR1Response;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useGstr3b(
  params: Gstr3bJsonApiGstGstr3bGetParams,
  options?: { enabled?: boolean },
): UseQueryResult<GSTR3BResponse, Error> {
  return useQuery({
    queryKey: gstQueryKeys.gstr3b(params),
    queryFn: async () => {
      const res = await gstr3bJsonApiGstGstr3bGet(params, { credentials: "include" });
      const status = res.status as number;
      if (status === 401 || status === 403) {
        throw new Error("You need an admin session to view GSTR-3B.");
      }
      if (status === 400) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || "Invalid period.");
      }
      if (status !== 200) throw new Error(`GSTR-3B failed (${status})`);
      return res.data as GSTR3BResponse;
    },
    enabled: options?.enabled ?? true,
  });
}

export const BRANCHES_LIST_QK = ["branches", "list"] as const;

export function useBranchesList(options?: {
  enabled?: boolean;
}): UseQueryResult<BranchRead[], Error> {
  return useQuery({
    queryKey: BRANCHES_LIST_QK,
    queryFn: async () => {
      const res = await listBranchesApiBranchesGet(
        { page: 1, page_size: 200, status: RecordStatus.active },
        { credentials: "include" },
      );
      const status = res.status as number;
      if (status !== 200) throw new Error(`Branches failed (${status})`);
      const list = res.data as { items: BranchRead[] };
      return list.items;
    },
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateUser(id: string): UseMutationResult<UserRead, Error, UserAdminUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserAdminUpdate) => {
      const res = await adminUpdateUserApiUsersUserIdPatch(id, body, {
        credentials: "include",
      });
      const status = res.status as number;
      if (status === 403) throw new Error("Admin only.");
      if (status !== 200) throw new Error(`Update failed (${status})`);
      return res.data as UserRead;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: USERS_LIST_QK });
      void qc.invalidateQueries({ queryKey: USER_ME_QK });
    },
  });
}

// ---------------------------------------------------------------- cash book (V50 A2)

export const cashBookQueryKeys = {
  all: ["cash-book"] as const,
  categories: (q: string) => ["cash-book", "categories", q] as const,
  expenses: (p: ListExpensesApiCashBookExpensesGetParams) => ["cash-book", "expenses", p] as const,
  summary: (from: string, to: string) => ["cash-book", "summary", from, to] as const,
};

export function useExpenseCategories(q: string): UseQueryResult<ExpenseCategoryRead[], Error> {
  return useQuery({
    queryKey: cashBookQueryKeys.categories(q),
    queryFn: async () => {
      const res = await listCategoriesApiCashBookCategoriesGet(q ? { q } : {}, {
        credentials: "include",
      });
      if (res.status !== 200) throw new Error(`Failed to load categories (${res.status})`);
      return res.data as ExpenseCategoryRead[];
    },
  });
}

export function useCreateExpenseCategory(): UseMutationResult<
  ExpenseCategoryRead,
  Error,
  ExpenseCategoryCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ExpenseCategoryCreate) => {
      const res = await createCategoryApiCashBookCategoriesPost(body, { credentials: "include" });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Create category failed (${res.status})`);
      }
      return res.data as ExpenseCategoryRead;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: cashBookQueryKeys.all }),
  });
}

export function useExpenses(
  params: ListExpensesApiCashBookExpensesGetParams,
  opts?: { enabled?: boolean },
): UseQueryResult<ExpenseList, Error> {
  return useQuery({
    queryKey: cashBookQueryKeys.expenses(params),
    queryFn: async () => {
      const res = await listExpensesApiCashBookExpensesGet(params, { credentials: "include" });
      if (res.status !== 200) throw new Error(`Failed to load expenses (${res.status})`);
      return res.data as ExpenseList;
    },
    enabled: opts?.enabled ?? true,
  });
}

export function useCreateExpense(): UseMutationResult<ExpenseRead, Error, ExpenseCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ExpenseCreate) => {
      const res = await createExpenseApiCashBookExpensesPost(body, { credentials: "include" });
      if (res.status !== 201) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Save expense failed (${res.status})`);
      }
      return res.data as ExpenseRead;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: cashBookQueryKeys.all }),
  });
}

export function useDeleteExpense(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await deleteExpenseApiCashBookExpensesExpenseIdDelete(id, {
        credentials: "include",
      });
      if ((res.status as number) !== 204) {
        const detail = (res.data as { detail?: string })?.detail;
        throw new Error(detail || `Delete expense failed (${res.status})`);
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: cashBookQueryKeys.all }),
  });
}

export function useCashSummary(from: string, to: string): UseQueryResult<CashSummary, Error> {
  return useQuery({
    queryKey: cashBookQueryKeys.summary(from, to),
    queryFn: async () => {
      const res = await summaryApiCashBookSummaryGet(
        { from_date: from, to_date: to },
        { credentials: "include" },
      );
      if (res.status !== 200) throw new Error(`Failed to load summary (${res.status})`);
      return res.data as CashSummary;
    },
    enabled: Boolean(from && to),
  });
}
